import { useEffect, useRef } from 'react';

import lottie, { type AnimationItem } from 'lottie-web';

interface VideaLoaderProps {
  /** Height in pixels or CSS value. Defaults to 340 */
  height?: number | string;
  /** Width in pixels or CSS value. Defaults to 600 */
  width?: number | string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Additional className */
  className?: string;
}

/**
 * Videa branded loading animation using lottie-web directly.
 *
 * This component renders the Lottie animation outside of React's state management,
 * avoiding the performance issues caused by @lottiefiles/react-lottie-player
 * which calls setState on every animation frame.
 */
export const VideaLoader = ({
  height = 340,
  width = 600,
  style,
  className,
}: VideaLoaderProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Load the animation - runs entirely outside React
    animationRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: '/assets/images/videa-load.json',
    });

    // Cleanup on unmount
    return () => {
      animationRef.current?.destroy();
      animationRef.current = null;
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        height: typeof height === 'number' ? `${height}px` : height,
        width: typeof width === 'number' ? `${width}px` : width,
        ...style,
      }}
    />
  );
};
